import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from "framer-motion";
import {
  Moon,
  Droplet,
  Flame,
  Dumbbell,
  BatteryCharging,
  Heart,
  Zap,
  Plus,
  Minus,
  Sparkles,
} from "lucide-react";

const tabs = [
  { id: "sleep", label: "Sleep", Icon: Moon, color: "#6366F1" },
  { id: "water", label: "Water", Icon: Droplet, color: "#0EA5E9" },
  { id: "calories", label: "Calories", Icon: Flame, color: "#FF6B00" },
  { id: "workout", label: "Workout", Icon: Dumbbell, color: "#10B981" },
];

const reveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

// Animated number hook
function AnimatedNumber({ value, decimals = 0, suffix = "", className = "" }) {
  const mv = useMotionValue(value);
  const rounded = useTransform(mv, (v) =>
    decimals === 0 ? Math.round(v).toString() : v.toFixed(decimals)
  );
  useEffect(() => {
    const c = animate(mv, value, { duration: 0.8, ease: "easeOut" });
    return c.stop;
  }, [value, mv]);
  return (
    <span className={className}>
      <motion.span>{rounded}</motion.span>
      {suffix}
    </span>
  );
}

// Animated circular ring
function Ring({ value, color, size = 128, stroke = 10, label, sub }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={stroke} />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          initial={false}
          animate={{ strokeDashoffset: c - (Math.max(0, Math.min(100, value)) / 100) * c }}
          transition={{ duration: 0.9, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <AnimatedNumber
          value={Math.round(value)}
          className="font-heading font-bold text-3xl text-white leading-none"
        />
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 mt-1">{label}</div>
        {sub && <div className="text-[10px] text-white/80 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function Stepper({ value, onChange, min, max, step = 1, suffix, testid }) {
  return (
    <div className="flex items-center justify-between bg-zoku-bg rounded-2xl px-2 py-2">
      <button
        onClick={() => onChange(Math.max(min, +(value - step).toFixed(2)))}
        className="w-10 h-10 rounded-xl bg-white border border-stone-200 flex items-center justify-center text-zoku-ink hover:border-zoku-orange hover:text-zoku-orange active:scale-95 transition"
        data-testid={`${testid}-dec`}
        aria-label="decrease"
      >
        <Minus className="w-4 h-4" />
      </button>
      <div className="text-center">
        <AnimatedNumber
          value={value}
          decimals={step < 1 ? 1 : 0}
          suffix={suffix}
          className="font-heading text-3xl font-bold text-zoku-ink leading-none"
        />
      </div>
      <button
        onClick={() => onChange(Math.min(max, +(value + step).toFixed(2)))}
        className="w-10 h-10 rounded-xl bg-white border border-stone-200 flex items-center justify-center text-zoku-ink hover:border-zoku-orange hover:text-zoku-orange active:scale-95 transition"
        data-testid={`${testid}-inc`}
        aria-label="increase"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}

// Insight engine (light, simple formulas — for visual demo only)
function computeInsights({ sleep, water, calories, workoutMin, intensity }) {
  const sleepScore = Math.max(0, Math.min(100, ((sleep - 4) / 5) * 100));
  const hydrationScore = Math.max(0, Math.min(100, (water / 8) * 100));
  const fuelScore = Math.max(
    0,
    Math.min(100, 100 - Math.abs(calories - 2000) / 12)
  );
  const intensityMap = { light: 0.6, moderate: 1, hard: 1.6 };
  const strain = Math.max(
    0,
    Math.min(21, (workoutMin / 60) * 9 * (intensityMap[intensity] || 1))
  );

  const recovery = Math.round(sleepScore * 0.55 + hydrationScore * 0.25 + fuelScore * 0.2);
  const bodyBattery = Math.round(
    sleepScore * 0.45 + hydrationScore * 0.2 + fuelScore * 0.15 + (100 - (strain / 21) * 100) * 0.2
  );

  let advice = "Solid baseline — keep it steady today.";
  let tone = "good";
  if (bodyBattery < 40) {
    advice = "Battery's low. Skip the heavy lift, take a walk and hit 9 hours tonight.";
    tone = "warn";
  } else if (bodyBattery < 65) {
    advice = "Green zone for moderate training. Hydrate 2 more glasses before noon.";
    tone = "ok";
  } else if (strain > 15) {
    advice = "Big day logged. Prioritise protein at dinner and cut caffeine after 2 PM.";
    tone = "ok";
  } else if (recovery > 80) {
    advice = "Recovery is strong — today's a great window for a hard session.";
    tone = "good";
  }

  return { recovery, bodyBattery, strain: +strain.toFixed(1), advice, tone };
}

export default function InteractiveDemo() {
  const [tab, setTab] = useState("sleep");
  const [sleep, setSleep] = useState(7.5);
  const [water, setWater] = useState(5);
  const [calories, setCalories] = useState(1800);
  const [workoutMin, setWorkoutMin] = useState(45);
  const [intensity, setIntensity] = useState("moderate");

  const insights = useMemo(
    () => computeInsights({ sleep, water, calories, workoutMin, intensity }),
    [sleep, water, calories, workoutMin, intensity]
  );

  return (
    <section
      id="try"
      className="py-24 md:py-32 bg-white relative overflow-hidden"
      data-testid="interactive-demo-section"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 left-1/4 w-[540px] h-[540px] rounded-full bg-zoku-orangeLight blur-3xl opacity-60" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <motion.div {...reveal} className="max-w-3xl">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-zoku-orange mb-4">
            Try Zoku — live
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-zoku-ink">
            Log it, and watch your{" "}
            <span className="font-editorial text-zoku-orange">body reply.</span>
          </h2>
          <p className="mt-5 text-lg text-zoku-ink2 max-w-xl">
            Tap through sleep, water, calories and workouts. Zoku runs the maths and tells you what
            to do next — body battery, recovery, strain, fuel.
          </p>
        </motion.div>

        <div className="mt-14 grid lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Left: inputs */}
          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.1 }}
            className="lg:col-span-6 bg-zoku-bg rounded-[2rem] border border-stone-100 p-6 md:p-8"
          >
            {/* Tabs */}
            <div className="flex flex-wrap gap-2" data-testid="demo-tabs">
              {tabs.map((t) => {
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-all active:scale-95 ${
                      active
                        ? "bg-zoku-ink text-white shadow-sm"
                        : "bg-white border border-stone-200 text-zoku-ink2 hover:border-zoku-orange hover:text-zoku-orange"
                    }`}
                    data-testid={`demo-tab-${t.id}`}
                  >
                    <t.Icon className="w-4 h-4" style={{ color: active ? t.color : undefined }} />
                    {t.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-8 min-h-[260px]">
              <AnimatePresence mode="wait">
                {tab === "sleep" && (
                  <motion.div
                    key="sleep"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                    data-testid="demo-panel-sleep"
                  >
                    <label className="text-sm text-zoku-ink2">Last night's sleep</label>
                    <div className="mt-3 grid grid-cols-[1fr_auto] gap-4 items-center">
                      <input
                        type="range"
                        min="4"
                        max="10"
                        step="0.25"
                        value={sleep}
                        onChange={(e) => setSleep(parseFloat(e.target.value))}
                        style={{ "--v": `${((sleep - 4) / 6) * 100}%` }}
                        className="zoku-range"
                        data-testid="demo-sleep-slider"
                      />
                      <div className="font-heading text-3xl font-bold text-zoku-ink leading-none w-24 text-right">
                        <AnimatedNumber value={sleep} decimals={2} suffix="h" />
                      </div>
                    </div>
                    <div className="mt-6 grid grid-cols-4 gap-2">
                      {["Awake", "REM", "Core", "Deep"].map((s, i) => {
                        const pcts = [8, 22, 52, 18];
                        return (
                          <div key={s} className="bg-white rounded-xl p-3 border border-stone-100">
                            <div className="text-[10px] uppercase tracking-wider text-zoku-ink3">
                              {s}
                            </div>
                            <div className="mt-1 font-heading text-base font-bold text-zoku-ink">
                              {Math.round((sleep * 60 * pcts[i]) / 100)}m
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {tab === "water" && (
                  <motion.div
                    key="water"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                    data-testid="demo-panel-water"
                  >
                    <label className="text-sm text-zoku-ink2">Glasses of water today</label>
                    <div className="mt-3">
                      <Stepper
                        value={water}
                        onChange={setWater}
                        min={0}
                        max={12}
                        suffix=" glasses"
                        testid="demo-water"
                      />
                    </div>
                    <div className="mt-6 grid grid-cols-8 gap-2">
                      {Array.from({ length: 8 }).map((_, i) => {
                        const filled = i < water;
                        return (
                          <motion.div
                            key={i}
                            animate={{ scale: filled ? [1, 1.1, 1] : 1 }}
                            transition={{ duration: 0.35 }}
                            className={`h-14 rounded-xl border flex items-end justify-center pb-1 ${
                              filled
                                ? "bg-sky-100 border-sky-300"
                                : "bg-white border-stone-200"
                            }`}
                          >
                            <Droplet
                              className={`w-4 h-4 ${filled ? "text-sky-500 fill-sky-400" : "text-stone-300"}`}
                            />
                          </motion.div>
                        );
                      })}
                    </div>
                    <p className="mt-3 text-xs text-zoku-ink3">
                      Goal · 8 glasses (~2L). Tap + to log a sip.
                    </p>
                  </motion.div>
                )}

                {tab === "calories" && (
                  <motion.div
                    key="cal"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                    data-testid="demo-panel-calories"
                  >
                    <label className="text-sm text-zoku-ink2">Calories logged today</label>
                    <div className="mt-3 grid grid-cols-[1fr_auto] gap-4 items-center">
                      <input
                        type="range"
                        min="800"
                        max="3200"
                        step="50"
                        value={calories}
                        onChange={(e) => setCalories(parseInt(e.target.value, 10))}
                        style={{ "--v": `${((calories - 800) / 2400) * 100}%` }}
                        className="zoku-range"
                        data-testid="demo-cal-slider"
                      />
                      <div className="font-heading text-3xl font-bold text-zoku-ink leading-none w-32 text-right">
                        <AnimatedNumber value={calories} suffix=" kcal" />
                      </div>
                    </div>
                    <div className="mt-6 grid grid-cols-3 gap-2">
                      {[
                        { l: "Protein", v: Math.round(calories * 0.0012 * 30), u: "g", c: "#FF6B00" },
                        { l: "Carbs", v: Math.round(calories * 0.0012 * 45), u: "g", c: "#10B981" },
                        { l: "Fat", v: Math.round(calories * 0.0012 * 25), u: "g", c: "#6366F1" },
                      ].map((m) => (
                        <div key={m.l} className="bg-white rounded-xl p-4 border border-stone-100">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ background: m.c }}
                          />
                          <div className="mt-2 text-[10px] uppercase tracking-wider text-zoku-ink3">
                            {m.l}
                          </div>
                          <div className="font-heading text-xl font-bold text-zoku-ink">
                            {m.v}
                            <span className="text-xs text-zoku-ink3 ml-0.5">{m.u}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {tab === "workout" && (
                  <motion.div
                    key="wo"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                    data-testid="demo-panel-workout"
                  >
                    <label className="text-sm text-zoku-ink2">Workout duration</label>
                    <div className="mt-3 grid grid-cols-[1fr_auto] gap-4 items-center">
                      <input
                        type="range"
                        min="0"
                        max="120"
                        step="5"
                        value={workoutMin}
                        onChange={(e) => setWorkoutMin(parseInt(e.target.value, 10))}
                        style={{ "--v": `${(workoutMin / 120) * 100}%` }}
                        className="zoku-range"
                        data-testid="demo-workout-slider"
                      />
                      <div className="font-heading text-3xl font-bold text-zoku-ink leading-none w-28 text-right">
                        <AnimatedNumber value={workoutMin} suffix=" min" />
                      </div>
                    </div>
                    <div className="mt-6">
                      <div className="text-sm text-zoku-ink2 mb-2">Intensity</div>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { id: "light", l: "Light", d: "Walk, yoga" },
                          { id: "moderate", l: "Moderate", d: "Jog, lift" },
                          { id: "hard", l: "Hard", d: "HIIT, race" },
                        ].map((o) => {
                          const active = intensity === o.id;
                          return (
                            <button
                              key={o.id}
                              onClick={() => setIntensity(o.id)}
                              className={`rounded-2xl p-4 text-left transition-all active:scale-95 ${
                                active
                                  ? "bg-zoku-orange text-white border border-zoku-orange shadow-sm"
                                  : "bg-white border border-stone-200 text-zoku-ink2 hover:border-zoku-orange"
                              }`}
                              data-testid={`demo-intensity-${o.id}`}
                            >
                              <div className="font-semibold text-sm">{o.l}</div>
                              <div
                                className={`text-[11px] mt-0.5 ${
                                  active ? "text-white/80" : "text-zoku-ink3"
                                }`}
                              >
                                {o.d}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Right: live insights */}
          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.15 }}
            className="lg:col-span-6 bg-zoku-ink text-white rounded-[2rem] p-6 md:p-8 relative overflow-hidden"
            data-testid="demo-insights-panel"
          >
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-zoku-orange/30 blur-3xl" />
            <div className="relative">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60">
                <Sparkles className="w-3.5 h-3.5 text-zoku-orange" /> Live insights
              </div>

              <div className="mt-6 grid grid-cols-3 gap-4 place-items-center">
                <div className="flex flex-col items-center">
                  <Ring
                    value={insights.bodyBattery}
                    color="#FF6B00"
                    label="Battery"
                    sub={
                      insights.bodyBattery > 70
                        ? "Charged"
                        : insights.bodyBattery > 40
                        ? "Topping up"
                        : "Drained"
                    }
                  />
                </div>
                <div>
                  <Ring value={insights.recovery} color="#10B981" label="Recovery" />
                </div>
                <div>
                  <Ring
                    value={(insights.strain / 21) * 100}
                    color="#6366F1"
                    label="Strain"
                    sub={`${insights.strain}`}
                  />
                </div>
              </div>

              {/* Advice */}
              <motion.div
                key={insights.advice}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="mt-8 bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5 flex gap-4"
                data-testid="demo-advice"
              >
                <div className="shrink-0 w-10 h-10 rounded-xl bg-zoku-orange flex items-center justify-center">
                  <BatteryCharging className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wider text-white/50 mb-1">
                    Zoku Coach says
                  </div>
                  <p className="text-white/95 leading-relaxed">{insights.advice}</p>
                </div>
              </motion.div>

              {/* Secondary stats */}
              <div className="mt-5 grid grid-cols-3 gap-3">
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/60">
                    <Heart className="w-3 h-3 text-emerald-400" /> HRV
                  </div>
                  <div className="mt-1 font-heading text-lg font-bold">
                    <AnimatedNumber
                      value={42 + Math.round((insights.recovery - 60) * 0.2)}
                      suffix=" ms"
                    />
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/60">
                    <Zap className="w-3 h-3 text-zoku-orange" /> RHR
                  </div>
                  <div className="mt-1 font-heading text-lg font-bold">
                    <AnimatedNumber
                      value={60 - Math.round((insights.recovery - 60) * 0.08)}
                      suffix=" bpm"
                    />
                  </div>
                </div>
                <div className="bg-white/5 rounded-xl p-4">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/60">
                    <Flame className="w-3 h-3 text-orange-300" /> Fuel
                  </div>
                  <div className="mt-1 font-heading text-lg font-bold">
                    <AnimatedNumber
                      value={Math.max(
                        0,
                        Math.min(100, 100 - Math.abs(calories - 2000) / 12)
                      )}
                      suffix="%"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
